import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppState } from "../../RootReducer";
import { getMethods } from "../../selectors/payments-selectors";
import { getActiveCurrency } from "../../selectors/user-selectors";
import { selectMethod } from "../../payments/PaymentsActions";
import Loader from "../../layout/Loader";
import CryptoChillWithdraw from "../../ui/paymentproviders/CryptoChillWithdrawPayment";
import { resetWithdrawContext } from "../../payments/WithdrawActions";
import { CryptoSelects } from "./CryptoSelects";
import "./CryptoMethods.scss";
import { ContextStatus, WithdrawPhase, WithdrawStateStatus } from "../../payments/PaymentsReducer";
import { t } from "../../i18n";
import { WithdrawPermitted } from "../../http/protocol";
import { BrandLink } from "../../ui/buttons/buttons";

interface CryptoWithdrawMethodsProps {
  groupName?: string;
  handleClickOk?: () => void;
  phase?: WithdrawPhase;
  hideCashier?: () => void;
  isKycRequirementsNotMet?: boolean;
}

const CryptoWithdrawMethods: React.FC<CryptoWithdrawMethodsProps> = ({ groupName, handleClickOk, phase, hideCashier, isKycRequirementsNotMet }) => {
  const dispatch = useDispatch();
  const methods = useSelector((state: AppState) => getMethods(state));
  const groupMethods = methods?.filter((m) => m.group === groupName) ?? [];
  const activeSiteCurrency = useSelector((state: AppState) => getActiveCurrency(state));
  const activeMethod = useSelector((state: AppState) => state.payments.withdraw.activeMethod);
  const withdraw = useSelector((state: AppState) => state.payments.withdraw);
  const [loadingNetwork, setLoadingNetwork] = useState(false);
  const contextStatus = withdraw.withdrawContextStatus;
  const withdrawStatus = withdraw.status;
  const permitted = withdraw.withdrawContext?.permitted;

  const onNetworkChange = async (networkCode: string | undefined, selectedMethodCode: string | undefined) => {
    if (!networkCode || !activeSiteCurrency?.code) return;

    setLoadingNetwork(true);
    const selectedMethodCopy = { ...groupMethods.find((m) => m.code === selectedMethodCode) };
    selectedMethodCopy.code = networkCode;
    await dispatch(selectMethod(selectedMethodCopy, activeSiteCurrency.code) as any);
    setLoadingNetwork(false);
  };

  const onMethodChange = async (methodCode: string | undefined) => {
    if (!methodCode || !activeSiteCurrency?.code) return;
    dispatch(resetWithdrawContext());
  };

  if (permitted === WithdrawPermitted.DEPOSITS_WAITING_TO_CLEAR || permitted === WithdrawPermitted.REQUEST_ALREADY_OPEN) {
    const errorMessage =
      permitted === WithdrawPermitted.REQUEST_ALREADY_OPEN ? t("withdraw.context-error.REQUEST_ALREADY_OPEN") : t("withdraw.waiting-for-deposit");
    return (
      <>
        <div className="alert alert-danger">{errorMessage}</div>
      </>
    );
  }
  if (permitted === WithdrawPermitted.BONUS_ACTIVE) {
    return (
      <>
        <div className={"alert alert-danger"}>{t(`withdraw.context-error.BONUS_ACTIVE`)}</div>
        <BrandLink to={"/account/bonus"} onClick={() => hideCashier?.()} className={"btn btn-primary full-width"}>
          {t("withdraw.go-to-bonuses")}
        </BrandLink>
      </>
    );
  }

  return (
    <>
      <div className="group-specific-methods" role="region">
        {phase !== WithdrawPhase.CONFIRM_WITHDRAW && phase !== WithdrawPhase.WITHDRAW_RESPONSE && (
          <div className="method-selector">
            <CryptoSelects
              groupMethods={groupMethods}
              onMethodChange={onMethodChange}
              onNetworkChange={onNetworkChange}
              initialValue={activeMethod?.code}
              latestUsedMethodCode={groupMethods.find((m) => m.isLatest || m.isLatestCryptoWithdraw)?.code}
            />
          </div>
        )}
        {contextStatus !== ContextStatus.ERROR && !isKycRequirementsNotMet && (
          <>
            {activeMethod?.code && !loadingNetwork ? (
              <CryptoChillWithdraw withdraw={withdraw} handleClickOk={handleClickOk} />
            ) : (
              loadingNetwork && (
                <div className="crypto-group-methods-loader-container">
                  <Loader />
                </div>
              )
            )}
          </>
        )}
      </div>
      {contextStatus === ContextStatus.ERROR && !isKycRequirementsNotMet && (
        <div className="contextError">
          <p className="text-danger" style={{ textAlign: "center" }}>
            {t("deposit.context-error")}
          </p>
        </div>
      )}
      {contextStatus === ContextStatus.ERROR && withdrawStatus === WithdrawStateStatus.WITHDRAW_ERROR_INITIALIZING && !isKycRequirementsNotMet && (
        <div className={"deposit-content"}>
          <p className="text-danger" style={{ textAlign: "center" }}>
            {t("deposit.context-error")}
          </p>
        </div>
      )}
    </>
  );
};

export default CryptoWithdrawMethods;
